import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import { useScreenSizeOnly } from 'hooks/useScreenSize'
import PreviewableModal from '../../../../Tripkit/PreviewableModal/PreviewableModal'
import DesktopOfferMapCard from './DesktopOfferMapCard'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalContent from 'LuxKit/Modal/ModalContent'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineGlobeIcon from 'LuxKit/Icons/line/LineGlobeIcon'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import TextButton from 'LuxKit/Button/TextButton'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import TripContext from 'tripPlanner/contexts/TripContext'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import ExperienceBookmarkToItineraryButton from 'tripPlanner/components/Bookmark/BookmarkButton/Experience/ExperienceBookmarkToItineraryButton'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import MarkdownRender from 'LuxKit/MarkdownRender'
import type { ExperienceItem } from 'tripPlanner/types/tripItem'
import { buildMyEscapesOrderLink, isLeBooking, isLeBookmark } from 'tripPlanner/utils'
import useTripPlannerCTALabels from 'tripPlanner/hooks/useTripPlannerCTALabels'
import SavedItemRemoveButton from 'tripPlanner/components/ItemCard/Common/SavedItemRemoveButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import NotesAndAttachments from './NotesAndAttachments'
import Divider from 'LuxKit/Divider'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'

const SrcImage = styled.img`
  height: ${rem(120)};
  width: auto;
`

const CarouselContainer = styled.div`
  padding-bottom: ${rem(20)};
`

const Flex1 = styled.div`
  flex: 1;
`

interface Props {
  experience: App.ExperienceOffer
  tripItem?: ExperienceItem
  onClose: () => void
}

function LEExperienceSummaryCard({ experience, tripItem, onClose }: Props) {
  const intl = useIntl()
  const { bookingDetailsCopy } = useTripPlannerCTALabels()
  const isMobile = useScreenSizeOnly('mobile')
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const canEdit = useCanEditTrip()
  const tripContext = useContext(TripContext)
  const tripId = tripContext?.tripId

  const readMoreLabel = intl.formatMessage({
    id: 'Q67ZV_FIdclHuhHYKS4G',
    defaultMessage: 'Read more',
    description: 'Button to expand and read the full experience description',
  })
  const readLessLabel = intl.formatMessage({
    id: '4dQS-8kYXNFUqPnA3OjP',
    defaultMessage: 'Read less',
    description: 'Button to collapse an expanded experience description',
  })

  const onClickViewOffer = useCallback(() => {
    if (tripId) {
      setImmersiveMode({
        tripId,
      })
    }
  }, [tripId, setImmersiveMode])

  const carousel = <CardCarousel gap={8} gutterSize={20} desktopGutterSize={24} gutterStyle="overflow">
    {experience.images.map(image => (
      image.id ?
          <Image key={image.id} id={image.id} alt={image.title} height={120} /> :
          <SrcImage key={image.url} src={image.url} alt={image.title} />
    ))}
  </CardCarousel>

  const included = experience.copy.included?.trim()

  const bookingID = tripItem && isLeBooking(tripItem) && canEdit ? tripItem.confirmationCode : undefined

  const offerLink = (tripItem && isLeBookmark(tripItem) && buildProductLink(tripItem, { experience })) || getExperienceOfferPageURL(experience)

  const bookmarkButton = tripItem ? <SavedItemRemoveButton item={tripItem} /> : <ExperienceBookmarkButton offer={experience} withLabel="never" size="small" />

  const mainContent = <>
    <Group direction="horizontal" gap={8}>
      <Flex1>
        <VerticalSpacer gap={8}>
          {experience.location.name && <BodyTextBlock
            variant="medium"
            startIcon={<LineMapMarkerIcon />}
          >
            {experience.location.name}
          </BodyTextBlock>}
          {experience.vendor?.website && (
            <Clickable
              href={experience.vendor.website}
              target="_blank"
              rel="noopener noreferrer"
            >
              <BodyTextBlock
                variant="medium"
                startIcon={<LineGlobeIcon />}
                weight="bold"
                wrap="word-wrap"
              >
                {experience.vendor.website}
              </BodyTextBlock>
            </Clickable>
          )}
          {tripItem?.isBooked && <CSSBreakpoint max="mobile">
            <BookedLabel />
          </CSSBreakpoint>}
        </VerticalSpacer>
      </Flex1>
      <CSSBreakpoint max="mobile">
        {bookmarkButton}
      </CSSBreakpoint>
    </Group>

    <LargeContentToggle
      maxHeight={80}
      collapsedHeight={60}
      showMoreText={readMoreLabel}
      showLessText={readLessLabel}
      showChevron={false}
    >
      <VerticalSpacer gap={12}>
        <MarkdownRender type="compact" content={experience.copy.dealDescription ?? experience.copy.description} fontSize="medium" />
        {included && <>
          <BodyText as="h6" variant="large" weight="bold">
            {intl.formatMessage({
              id: '-HrkMzd-_kOufUf-RdkZ',
              defaultMessage: 'Inclusions',
              description: 'Heading for the list of included items in an experience offer',
            })}
          </BodyText>
          <MarkdownRender type="compact" content={included} fontSize="medium" />
        </>}
      </VerticalSpacer>
    </LargeContentToggle>

    {tripItem && <>
      <Divider kind="primary" />
      <NotesAndAttachments item={tripItem} canEdit={canEdit} />
    </>}
  </>

  const footer = <ModalFooter>
    <Group direction="vertical" desktopDirection="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="start" desktopVerticalAlign="center" wrap="wrap">
      {!tripItem?.isBooked && <Group direction="vertical" verticalAlign="end">
        <PriceRowPriceCaption>
          {intl.formatMessage({
            id: '1XweaLIWOijLtP7TA0Na',
            defaultMessage: 'From',
            description: 'Price caption prefix indicating starting price in the trip planner map view',
          })}
        </PriceRowPriceCaption>
        <PriceRowPrice
          size="L"
          price={experience.pricing.price}
          saleUnit={experience.pricing.saleUnit}
        />
        {experience.pricing.discountPercent >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && <PriceRowValueDiscountWithCaption
          size="L"
          originalValue={Math.ceil(experience.pricing.value)}
          discountPercentage={experience.pricing.discountPercent}
        />}
      </Group>}

      <Group direction="horizontal" horizontalAlign="center" gap={12} wrap="wrap">
        <TextButton kind="tertiary" size="large" to={offerLink} onClick={onClickViewOffer}>
          {intl.formatMessage({
            id: 'ytXofAGZ6w6EjJQ9rN0m',
            defaultMessage: 'View offer',
            description: 'Action button to view the full experience offer details',
          })}
        </TextButton>
        {!bookingID && <ExperienceBookmarkToItineraryButton
          offer={experience}
          variant="button"
          dropdownPlacement="top"
        />}
        {!!bookingID && tripItem && <TextButton
          kind="secondary"
          size="large"
          to={buildMyEscapesOrderLink(tripItem)}
        >
          {bookingDetailsCopy}
        </TextButton>}
      </Group>
    </Group>
  </ModalFooter>

  if (isMobile) {
    return (
      <PreviewableModal onRequestClose={onClose}>
        <ModalHeader onCloseButtonClick={onClose} title={experience.name} />
        <ModalBody>
          <ModalContent>
            <CarouselContainer>
              {carousel}
            </CarouselContainer>
            <VerticalSpacer gap={20}>
              {mainContent}
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        {footer}
      </PreviewableModal>
    )
  }

  return (
    <DesktopOfferMapCard>
      <ModalBody>
        <ModalContent>
          <CarouselContainer>
            {carousel}
          </CarouselContainer>
          <VerticalSpacer gap={20}>
            <HeadingTextBlock
              variant="heading5"
              endIcon={<Group direction="horizontal" gap={12} verticalAlign="center">
                {tripItem?.isBooked && <BookedLabel />}
                {bookmarkButton}
              </Group>}
              fit="stretch"
            >
              {experience.name}
            </HeadingTextBlock>
            {mainContent}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      {footer}
    </DesktopOfferMapCard>
  )
}

export default LEExperienceSummaryCard
